// apps/client/src/pages/offices/BrokerManagementPage.tsx
// Broker Identity Management - Google Enterprise Best Practice
// Provides UI for name changes, merges, duplicate detection, and audit trails

import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { db, functions } from '../../lib/firebase';
import { collection, query, onSnapshot, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Modal from '../../components/common/Modal';
import { Users, Loader2, UserCog, Settings2 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import ChangeBrokerNameModal from '../../components/brokers/ChangeBrokerNameModal';
import { adminPageDenied } from '../../config/adminAccess';

// --- Type Definitions ---
interface BrokerDocument {
  id: string;
  brokerId: string;
  companyId: string;
  currentName: string;
  legalName: string;
  email?: string;
  phone?: string;
  currentOfficeId: string | null;
  allOfficeIds: string[];
  status: 'active' | 'inactive' | 'pending';
  nameAliases: string[];
  nameHistory: BrokerNameHistory[];
  mergedFrom?: string[];
  mergedInto?: string;
  auditLog: BrokerAuditLog[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string;
}

interface BrokerNameHistory {
  name: string;
  effectiveFrom: Timestamp;
  effectiveTo: Timestamp | null;
  reason?: string;
  changedBy?: string;
}

interface BrokerAuditLog {
  action: 'created' | 'name_changed' | 'merged' | 'status_changed' | 'office_assigned' | 'office_removed';
  timestamp: Timestamp;
  performedBy: string;
  details: { [key: string]: any };
  ipAddress?: string;
}

// Shape returned by findDuplicateBrokers: a PAIR per finding, not a broker.
interface DuplicateBrokerSide {
  id: string;
  currentName: string;
  nameAliases: string[];
  status: string;
}

interface DuplicatePair {
  broker1: DuplicateBrokerSide;
  broker2: DuplicateBrokerSide;
  similarity: number;
  matchReason: string;
}

// changeBrokerName / mergeBrokers both report whether the BigQuery
// broker_display rewrite was suppressed to preserve pipeline annotations.
interface BrokerWriteResult {
  bigQueryRecordsUpdated?: number;
  bigQuerySkipped?: boolean;
  bigQuerySkipReason?: string;
}

interface OutletContextType {
  userData: {
    companyId: string;
    role: string;
    uid: string;
    displayName?: string;
    assignedOffices?: string[];
    canSeeAllOffices?: boolean;
  };
  offices: Array<{ id: string; name: string }>;
  userVisibleOffices: Array<{ id: string; name: string }>;
}

const BrokerManagementPage: React.FC = () => {
  const { userData, userVisibleOffices: offices } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();

  // State
  const [brokers, setBrokers] = useState<BrokerDocument[]>([]);
  const [filteredBrokers, setFilteredBrokers] = useState<BrokerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const [officeFilter, setOfficeFilter] = useState<string>('all');

  // Modal state
  const [showNameChangeModal, setShowNameChangeModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<BrokerDocument | null>(null);

  // Form state
  const [newName, setNewName] = useState('');
  const [nameChangeReason, setNameChangeReason] = useState('');
  const [mergeToBrokerId, setMergeToBrokerId] = useState('');
  const [mergeReason, setMergeReason] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicatePair[]>([]);
  const [saving, setSaving] = useState(false);

  // findDuplicateBrokers enforces these roles server-side; don't show
  // office_admin a button that can only permission-deny.
  const canFindDuplicates = userData?.role === 'super_admin' || userData?.role === 'administrator';

  // Load brokers from Firestore
  useEffect(() => {
    if (!userData?.companyId) {
      setLoading(false);
      return;
    }

    const brokersQuery = query(collection(db, `companies/${userData.companyId}/brokers`));
    const unsubscribe = onSnapshot(
      brokersQuery,
      (snapshot) => {
        const brokerData: BrokerDocument[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            nameAliases: data.nameAliases || [],
            nameHistory: data.nameHistory || [],
            auditLog: data.auditLog || [],
            allOfficeIds: data.allOfficeIds || [],
          } as BrokerDocument;
        });

        // Filter out merged brokers (they're hidden)
        let activeBrokers = brokerData.filter(b => !b.mergedInto);

        // Office scoping: office_admin only sees brokers from their assigned offices
        if (userData?.role === 'office_admin' && !userData?.canSeeAllOffices && userData?.assignedOffices?.length) {
          const myOffices = new Set(userData.assignedOffices);
          activeBrokers = activeBrokers.filter(b =>
            (b.currentOfficeId && myOffices.has(b.currentOfficeId)) ||
            b.allOfficeIds?.some(id => myOffices.has(id))
          );
        }

        setBrokers(activeBrokers);
        setFilteredBrokers(activeBrokers);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading brokers:', err);
        setError('Failed to load brokers');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userData?.companyId]);

  // Apply filters
  useEffect(() => {
    let filtered = [...brokers];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.currentName.toLowerCase().includes(query) ||
        b.legalName.toLowerCase().includes(query) ||
        b.nameAliases.some(alias => alias.toLowerCase().includes(query)) ||
        b.email?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    // Office filter
    if (officeFilter !== 'all') {
      filtered = filtered.filter(b => b.currentOfficeId === officeFilter);
    }

    // Sort alphabetically by current name
    filtered.sort((a, b) => a.currentName.localeCompare(b.currentName));

    setFilteredBrokers(filtered);
  }, [brokers, searchQuery, statusFilter, officeFilter]);

  // Explain what happened to the BigQuery commission rows. A suppressed
  // rewrite is a success, not a failure, so it must read as one.
  const describeBigQueryOutcome = (result: BrokerWriteResult | undefined): string => {
    if (result?.bigQuerySkipped) {
      const reason = result.bigQuerySkipReason ? ` (${result.bigQuerySkipReason})` : '';
      return `Commission records in BigQuery were left unchanged to preserve their pipeline annotations${reason}.`;
    }
    const updated = result?.bigQueryRecordsUpdated ?? 0;
    return `Updated ${updated} BigQuery commission record${updated === 1 ? '' : 's'}.`;
  };

  // Open the existing merge flow with a duplicate side as the merge SOURCE.
  const startMergeFromDuplicate = (side: DuplicateBrokerSide) => {
    const fullBroker = brokers.find(b => b.id === side.id);
    if (!fullBroker) {
      setError(`"${side.currentName}" is no longer available to merge. Re-run Find Duplicates.`);
      return;
    }
    setSelectedBroker(fullBroker);
    setMergeToBrokerId('');
    setMergeReason('');
    setShowDuplicatesModal(false);
    setShowMergeModal(true);
  };

  // Find duplicates
  const findDuplicates = async () => {
    try {
      setSaving(true);
      const findDuplicatesFunc = httpsCallable(functions, 'findDuplicateBrokers');
      const result: any = await findDuplicatesFunc({ companyId: userData.companyId });

      if (result.data.duplicates && result.data.duplicates.length > 0) {
        setDuplicates(result.data.duplicates);
        setShowDuplicatesModal(true);
        setSuccess(`Found ${result.data.duplicates.length} potential duplicates`);
      } else {
        setSuccess('No duplicates found');
      }
    } catch (err: any) {
      setError(`Failed to find duplicates: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Change broker name
  const handleChangeName = async () => {
    if (!selectedBroker || !newName.trim()) {
      setError('Please enter a new name');
      return;
    }

    try {
      setSaving(true);
      const changeBrokerNameFunc = httpsCallable(functions, 'changeBrokerName');
      const result = await changeBrokerNameFunc({
        companyId: userData.companyId,
        brokerId: selectedBroker.id,
        newName: newName.trim(),
        reason: nameChangeReason.trim() || undefined
      });

      setSuccess(
        `Successfully changed name from "${selectedBroker.currentName}" to "${newName}". ` +
        describeBigQueryOutcome(result.data as BrokerWriteResult)
      );
      setShowNameChangeModal(false);
      setSelectedBroker(null);
      setNewName('');
      setNameChangeReason('');
    } catch (err: any) {
      setError(`Failed to change name: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Merge brokers
  const handleMergeBrokers = async () => {
    if (!selectedBroker || !mergeToBrokerId) {
      setError('Please select a broker to merge into');
      return;
    }

    try {
      setSaving(true);
      const mergeBrokersFunc = httpsCallable(functions, 'mergeBrokers');
      const result = await mergeBrokersFunc({
        companyId: userData.companyId,
        sourceBrokerId: selectedBroker.id,
        targetBrokerId: mergeToBrokerId,
        reason: mergeReason.trim() || 'Duplicate broker'
      });

      setSuccess(
        `Successfully merged "${selectedBroker.currentName}" into selected broker. ` +
        describeBigQueryOutcome(result.data as BrokerWriteResult)
      );
      setShowMergeModal(false);
      setSelectedBroker(null);
      setMergeToBrokerId('');
      setMergeReason('');
    } catch (err: any) {
      setError(`Failed to merge brokers: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Get office name
  const getOfficeName = (officeId: string | null): string => {
    if (!officeId) return 'Unassigned';
    const office = offices.find(o => o.id === officeId);
    return office?.name || 'Unknown Office';
  };

  // Format timestamp
  const formatTimestamp = (timestamp: Timestamp): string => {
    if (!timestamp) return 'N/A';
    try {
      return timestamp.toDate().toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format audit detail value to be user-friendly
  const formatAuditValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return 'N/A';

    // Convert office IDs to office names
    if (key.toLowerCase().includes('officeid') && typeof value === 'string') {
      return getOfficeName(value);
    }

    // Format arrays nicely
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }

    // Format objects
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  // Format field name to be readable
  const formatFieldName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ')          // Replace underscores with spaces
      .replace(/id$/i, 'ID')       // Capitalize ID at the end
      .replace(/\boffice id\b/i, 'Office')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UserCog}
        title="Broker Management"
        subtitle="Manage broker identities, name changes, and duplicate records"
        actions={
          canFindDuplicates ? (
            <button onClick={findDuplicates} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Searching...' : 'Find Duplicates'}
            </button>
          ) : undefined
        }
      />

      {/* Alerts */}
      {error && (
        <div className="bg-red-900 p-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-200 hover:text-white">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-900 p-3 rounded mb-4 flex justify-between items-center">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-200 hover:text-white">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name, email, aliases..."
              className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Office Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Office</label>
            <select
              value={officeFilter}
              onChange={(e) => setOfficeFilter(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
            >
              <option value="all">All Offices</option>
              {offices.map(office => (
                <option key={office.id} value={office.id}>{office.name}</option>
              ))}
              <option value="">Unassigned</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-slate-400">
          Showing {filteredBrokers.length} of {brokers.length} brokers
        </div>
      </div>

      {/* Brokers Table */}
      {loading ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="text-left p-4 text-sm font-semibold text-slate-300">Current Name</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-300">Legal Name</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-300">Aliases</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-300">Office</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-300">Status</th>
                <th className="text-left p-4 text-sm font-semibold text-slate-300">Name Changes</th>
                <th className="text-right p-4 text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i} className="border-t border-slate-700">
                  <td className="p-4">
                    <div className="h-4 bg-slate-700 rounded animate-pulse w-32 mb-2"></div>
                    <div className="h-3 bg-slate-700 rounded animate-pulse w-24"></div>
                  </td>
                  <td className="p-4"><div className="h-4 bg-slate-700 rounded animate-pulse w-28"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-700 rounded animate-pulse w-16"></div></td>
                  <td className="p-4"><div className="h-6 bg-slate-700 rounded-full animate-pulse w-24"></div></td>
                  <td className="p-4"><div className="h-6 bg-slate-700 rounded-full animate-pulse w-16"></div></td>
                  <td className="p-4 text-center"><div className="h-4 bg-slate-700 rounded animate-pulse w-20 mx-auto"></div></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <div className="h-8 bg-slate-700 rounded animate-pulse w-24"></div>
                      <div className="h-8 bg-slate-700 rounded animate-pulse w-16"></div>
                      <div className="h-8 bg-slate-700 rounded animate-pulse w-16"></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filteredBrokers.length === 0 ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-slate-500 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No brokers found</h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            {searchQuery || statusFilter !== 'all' || officeFilter !== 'all'
              ? 'Try adjusting your filters or search terms to find brokers.'
              : 'Brokers will appear here once commission data is uploaded and processed.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700">
              <tr>
                <th className="text-left p-4 font-medium">Current Name</th>
                <th className="text-left p-4 font-medium">Legal Name</th>
                <th className="text-left p-4 font-medium">Aliases</th>
                <th className="text-left p-4 font-medium">Office</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-left p-4 font-medium">Name Changes</th>
                <th className="text-right p-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBrokers.map((broker, index) => (
                <tr
                  key={broker.id}
                  className={`border-t border-slate-700 hover:bg-slate-700 transition-colors ${
                    index % 2 === 0 ? 'bg-slate-800' : 'bg-slate-900'
                  }`}
                >
                  <td className="p-4">
                    <div className="font-medium">{broker.currentName}</div>
                    {broker.email && (
                      <div className="text-sm text-slate-400">{broker.email}</div>
                    )}
                  </td>
                  <td className="p-4 text-slate-300">{broker.legalName}</td>
                  <td className="p-4">
                    {broker.nameAliases.length > 1 ? (
                      <div className="text-sm text-slate-400">
                        {broker.nameAliases.length} aliases
                      </div>
                    ) : (
                      <span className="text-slate-500">None</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      broker.currentOfficeId
                        ? 'bg-blue-900 text-blue-200'
                        : 'bg-slate-700 text-slate-400'
                    }`}>
                      {getOfficeName(broker.currentOfficeId)}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 text-xs rounded-full font-semibold border ${
                      broker.status === 'active'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : broker.status === 'inactive'
                        ? 'bg-slate-600 text-slate-400 border-slate-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    }`}>
                      {broker.status}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    {broker.nameHistory.length > 0 ? (
                      <button
                        onClick={() => {
                          setSelectedBroker(broker);
                          setShowHistoryModal(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        {broker.nameHistory.length} change{broker.nameHistory.length !== 1 ? 's' : ''}
                      </button>
                    ) : (
                      <span className="text-slate-500 text-sm">No changes</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      {!adminPageDenied(userData, 'commission-settings') && (
                      <button
                        onClick={() => navigate(`/broker-commission-settings?broker=${broker.id}`)}
                        className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 transition-colors flex items-center gap-1"
                        title="Commission Settings"
                      >
                        <Settings2 className="w-3.5 h-3.5" />
                        Commissions
                      </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedBroker(broker);
                          setNewName(broker.currentName);
                          setShowNameChangeModal(true);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        Change Name
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBroker(broker);
                          setShowMergeModal(true);
                        }}
                        className="px-3 py-1 bg-violet-600 text-white text-sm rounded hover:bg-violet-700 transition-colors"
                      >
                        Merge
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBroker(broker);
                          setShowHistoryModal(true);
                        }}
                        className="px-3 py-1 bg-slate-600 text-white text-sm rounded hover:bg-slate-700 transition-colors"
                      >
                        History
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Name Change Modal */}
      {selectedBroker && (
        <ChangeBrokerNameModal
          show={showNameChangeModal}
          onClose={() => {
            setShowNameChangeModal(false);
            setSelectedBroker(null);
          }}
          broker={selectedBroker}
          companyId={userData.companyId}
          onSuccess={() => {
            setSuccess(`Successfully changed broker name`);
            setTimeout(() => setSuccess(''), 5000);
          }}
        />
      )}

      {/* Merge Modal */}
      {showMergeModal && selectedBroker && (
        <Modal
          title={`Merge Broker: ${selectedBroker.currentName}`}
          show={showMergeModal}
          onClose={() => {
            setShowMergeModal(false);
            setSelectedBroker(null);
            setMergeToBrokerId('');
            setMergeReason('');
          }}
        >
          <form onSubmit={(e) => { e.preventDefault(); handleMergeBrokers(); }}>
            <div className="mb-4 p-3 bg-violet-900/50 text-violet-200 border border-violet-700/50 rounded">
              <strong>Warning:</strong> This will merge "{selectedBroker.currentName}" into another broker.
              All commission records will be updated. This action cannot be undone.
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Merge Into Broker *
              </label>
              <select
                value={mergeToBrokerId}
                onChange={(e) => setMergeToBrokerId(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
                required
              >
                <option value="">Select a broker...</option>
                {brokers
                  .filter(b => b.id !== selectedBroker.id)
                  .map(broker => (
                    <option key={broker.id} value={broker.id}>
                      {broker.currentName} ({getOfficeName(broker.currentOfficeId)})
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={mergeReason}
                onChange={(e) => setMergeReason(e.target.value)}
                placeholder="e.g., Duplicate record, same person with different spelling"
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
                rows={3}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowMergeModal(false);
                  setSelectedBroker(null);
                  setMergeToBrokerId('');
                  setMergeReason('');
                }}
                className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !mergeToBrokerId}
                className="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Merging...' : 'Merge Brokers'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedBroker && (
        <Modal
          title={`History: ${selectedBroker.currentName}`}
          show={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedBroker(null);
          }}
        >
          <div className="space-y-6">
            {/* Name History */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Name History</h3>
              {selectedBroker.nameHistory.length > 0 ? (
                <div className="space-y-2">
                  {selectedBroker.nameHistory.map((history, index) => (
                    <div key={index} className="bg-slate-700 p-3 rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{history.name}</div>
                          {history.reason && (
                            <div className="text-sm text-slate-400 mt-1">{history.reason}</div>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">
                          {formatTimestamp(history.effectiveFrom)}
                          {history.effectiveTo && ` - ${formatTimestamp(history.effectiveTo)}`}
                        </div>
                      </div>
                      {history.changedBy && (
                        <div className="text-xs text-slate-500 mt-1">Changed by: {history.changedBy}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No name changes recorded</p>
              )}
            </div>

            {/* Aliases */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Name Aliases</h3>
              <div className="flex flex-wrap gap-2">
                {selectedBroker.nameAliases.map((alias, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm"
                  >
                    {alias}
                  </span>
                ))}
              </div>
            </div>

            {/* Audit Log */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Audit Trail</h3>
              {selectedBroker.auditLog.length > 0 ? (
                <div className="space-y-2">
                  {selectedBroker.auditLog.slice().reverse().map((log, index) => (
                    <div key={index} className="bg-slate-700 p-3 rounded text-sm">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium capitalize text-emerald-400">{log.action.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-slate-400">{formatTimestamp(log.timestamp)}</span>
                      </div>
                      <div className="text-slate-300 text-xs mb-1">
                        By: <span className="text-slate-400 font-mono text-[10px]">{log.performedBy}</span>
                      </div>
                      {Object.keys(log.details).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(log.details).map(([key, value]) => (
                            <div key={key} className="text-xs">
                              <span className="text-slate-400">{formatFieldName(key)}: </span>
                              <span className="text-slate-300 font-medium">{formatAuditValue(key, value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No audit trail available</p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setShowHistoryModal(false);
                setSelectedBroker(null);
              }}
              className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Duplicates Modal */}
      {showDuplicatesModal && (
        <Modal
          title="Potential Duplicate Brokers"
          show={showDuplicatesModal}
          onClose={() => {
            setShowDuplicatesModal(false);
            setDuplicates([]);
          }}
        >
          <div className="space-y-4">
            {duplicates.length > 0 ? (
              duplicates.map((pair, index) => (
                <div key={index} className="bg-slate-700 p-4 rounded">
                  <div className="flex justify-between items-center gap-3 mb-3">
                    <span className="text-sm text-slate-300">{pair.matchReason}</span>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {Math.round(pair.similarity * 100)}% match
                    </span>
                  </div>

                  <div className="space-y-2">
                    {[pair.broker1, pair.broker2].map((side) => (
                      <div key={side.id} className="flex justify-between items-start gap-3 bg-slate-800 p-3 rounded">
                        <div className="min-w-0">
                          <div className="font-medium break-words">{side.currentName}</div>
                          <div className="text-sm text-slate-400 mt-1">Status: {side.status}</div>
                          <div className="text-sm text-slate-400 break-words">
                            Aliases: {side.nameAliases.length > 0 ? side.nameAliases.join(', ') : 'None'}
                          </div>
                        </div>
                        <button
                          onClick={() => startMergeFromDuplicate(side)}
                          className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 transition-colors whitespace-nowrap"
                        >
                          Merge this
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-slate-400">No duplicates found</p>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                setShowDuplicatesModal(false);
                setDuplicates([]);
              }}
              className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BrokerManagementPage;
